import { lazy, Suspense } from "react";
import { Routes, Route } from "react-router-dom";
import SoraniqLandingPage from "@/pages/SoraniqLandingPage";
import { ClerkProvider, SignedIn, SignedOut, SignIn, useAuth, AuthenticateWithRedirectCallback } from "@clerk/clerk-react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ConvexReactClient } from "convex/react";
import { clerkAppearance } from "@/lib/clerkTheme";
import GoogleAuthButton from "@/components/GoogleAuthButton";

// Lazy-loaded authenticated shell & secondary pages for fast landing page delivery
const DashboardShell = lazy(() => import("@/components/DashboardShell"));
const AuthLayout = lazy(() => import("@/components/AuthLayout"));
const AdminRoute = lazy(() => import("@/components/AdminRoute"));

const TextToSpeechPage = lazy(() => import("@/pages/GeneratePage"));
const VideoDubbingPage = lazy(() => import("@/pages/VideoDubbingPage"));
const EnglishDubbingPage = lazy(() => import("@/pages/EnglishDubbingPage"));
const VoiceLibraryPage = lazy(() => import("@/pages/VoiceLibraryPage"));
const VoiceCloningPage = lazy(() => import("@/pages/VoiceCloningPage"));
const HistoryPage = lazy(() => import("@/pages/HistoryPage"));
const PricingPage = lazy(() => import("@/pages/PricingPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const CustomSignUpPage = lazy(() => import("@/pages/CustomSignUpPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/TermsOfServicePage"));
const RefundPolicyPage = lazy(() => import("@/pages/RefundPolicyPage"));
const AdminPage = lazy(() => import("@/pages/admin/AdminPage"));

const PageLoader = () => (
  <div className="flex-1 flex items-center justify-center min-h-[50vh] p-8">
    <div className="flex flex-col items-center gap-3">
      <div className="w-7 h-7 rounded-full border-2 border-[#0c0a09]/20 dark:border-white/20 border-t-[#0c0a09] dark:border-t-white animate-spin" />
      <span className="text-xs font-mono text-[#777169] dark:text-[#a8a29e]">Loading...</span>
    </div>
  </div>
);

const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY || "pk_test_ZGVjaWRpbmctcXVhZ2dhLTcwLmNsZXJrLmFjY291bnRzLmRldiQ";
const CONVEX_URL = (import.meta.env.VITE_CONVEX_URL as string) || "https://upbeat-scorpion-447.convex.cloud";
const convex = new ConvexReactClient(CONVEX_URL);

const SignInWithRedirect = () => {
  const params = new URLSearchParams(window.location.search);
  const redirectUrl = params.get('redirect_url') || '/dubbing';
  return (
    <AuthLayout title="Welcome back" subtitle="Please enter your account details to continue.">
      <div className="w-full space-y-4">
        <GoogleAuthButton redirectUrl={redirectUrl} mode="sign-in" />
        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 h-px bg-[#e7e5e4] dark:bg-[#292524]" />
          <span className="text-[10px] font-mono text-[#777169] dark:text-[#a8a29e] uppercase tracking-widest">or</span>
          <div className="flex-1 h-px bg-[#e7e5e4] dark:bg-[#292524]" />
        </div>
        <SignIn appearance={clerkAppearance} routing="hash" signUpUrl={`/sign-up?redirect_url=${encodeURIComponent(redirectUrl)}`} fallbackRedirectUrl={redirectUrl} forceRedirectUrl={redirectUrl} />
      </div>
    </AuthLayout>
  );
};


function useAuthForConvex() {
  const auth = useAuth();
  return {
    ...auth,
    getToken: async (options?: { template?: string; skipCache?: boolean }) => {
      try {
        const token = await auth.getToken(options);
        return token;
      } catch {
        // Fallback to standard token if custom 'convex' template is not created in Clerk
        try {
          return await auth.getToken({ skipCache: options?.skipCache });
        } catch {
          return null;
        }
      }
    }
  };
}

export default function App() {
  return (
    <ClerkProvider publishableKey={PUBLISHABLE_KEY}>
      <ConvexProviderWithClerk client={convex} useAuth={useAuthForConvex}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public routes available to everyone */}
            <Route path="/" element={<SoraniqLandingPage />} />
            <Route path="/soraniq" element={<SoraniqLandingPage />} />
            <Route path="/:countryCode/soraniq" element={<SoraniqLandingPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route
              path="/sso-callback"
              element={
                <div className="min-h-screen flex items-center justify-center bg-[#f5f5f5] dark:bg-[#0c0a09]">
                  <AuthenticateWithRedirectCallback
                    signInForceRedirectUrl="/dubbing"
                    signUpForceRedirectUrl="/dubbing"
                  />
                </div>
              }
            />

            <Route
              path="*"
              element={
                <>
                  <SignedOut>
                    <Routes>
                      <Route 
                        path="/sign-in/*" 
                        element={
                          <SignInWithRedirect />
                        } 
                      />
                      <Route path="/sign-up/*" element={<CustomSignUpPage />} />
                      <Route
                        path="*"
                        element={<SignInWithRedirect />}
                      />
                    </Routes>
                  </SignedOut>
                  <SignedIn>
                    <Routes>
                      <Route
                        path="/admin/*"
                        element={
                          <AdminRoute>
                            <AdminPage />
                          </AdminRoute>
                        }
                      />
                      <Route
                        path="*"
                        element={
                          <DashboardShell>
                            <Routes>
                              <Route path="/tts" element={<TextToSpeechPage />} />
                              <Route path="/dubbing" element={<VideoDubbingPage />} />
                              <Route path="/dubbing/en-ku" element={<EnglishDubbingPage />} />
                              <Route path="/en-dubbing" element={<EnglishDubbingPage />} />
                              <Route path="/clone" element={<VoiceCloningPage />} />
                              <Route path="/voices/clone" element={<VoiceCloningPage />} />
                              <Route path="/voices" element={<VoiceLibraryPage />} />
                              <Route path="/history" element={<HistoryPage />} />
                              <Route path="/pricing" element={<PricingPage />} />
                              <Route path="/billing" element={<BillingPage />} />
                              <Route path="/settings" element={<SettingsPage />} />
                              <Route path="*" element={<VideoDubbingPage />} />
                            </Routes>
                          </DashboardShell>
                        }
                      />
                    </Routes>
                  </SignedIn>
                </>
              }
            />
          </Routes>
        </Suspense>
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
}